import {
  assignValue,
  assignProps,
  mapResults,
  findResultInCategory,
  selectableResultWithDates,
  findSelected,
} from '@/utils/resultsHelper';
import { responseError } from '@/utils/errors';
import { states } from '@/constants/lifecycle';
import withDraft from '@/utils/draftHelper';
import actions from '../constants';
import { requestSources } from '../../constants';
import { findRequestSource, safeTransferType } from '../../state/state';
import crushRequestFields from '../../forms/state/state';
import findRequestDatasets from './state';

const updateRequestDictionaries = withDraft((draft, { category, code, codes }) => {
  const { item, source } = findRequestSource(draft, { category, code });
  source.dictionaries.forEach((dictionary) => (
    assignProps(dictionary, { selected: codes.includes(dictionary.code) })
  ));
  item.isDirty = true;
});

const fetchRequestDictionaries = withDraft((draft, { category, code, datasetCode }) => {
  const { source } = findRequestSource(draft, { category, code });
  source.code = datasetCode;
  source.readStatus = states.PENDING;
  source.readErrors = [];
});

const dictionariesToRequest = withDraft((draft, { category, code, result }) => {
  const { item, source } = findRequestSource(draft, { category, code });
  item.request_source = requestSources.DATASET;
  const identifiers = item.dictionaries.map(({ id }) => id);

  assignProps(source, {
    dictionaries: result.map((dictionary) => ({
      ...dictionary,
      selected: identifiers.includes(dictionary.id),
    })),
    readStatus: states.SUCCESS,
    readErrors: [],
  });
});

const datasetSettingsToRequest = withDraft((draft, {
  category,
  code,
  settings,
  namespaces,
  status,
  workflow,
}) => {
  const { item, source } = findRequestSource(draft, { category, code });
  item.transfer.transfer_type = safeTransferType(item, { settings }, draft.allowPmnTransfer);

  assignProps(source, {
    settings, status, namespaces, settingsReadStatus: states.SUCCESS,
  });
  if (workflow?.readStatus === states.SUCCESS) {
    crushRequestFields(item, workflow);
  }
});

const fetchRequestDatasetSettings = withDraft((draft, action) => {
  const { source } = findRequestSource(draft, action);
  assignProps(source, { settingsReadStatus: states.PENDING, readErrors: [] });
});

const fetchRequestCatalogue = withDraft((draft, action) => {
  const { source } = findRequestSource(draft, action);
  assignProps(source, { catalogueReadStatus: states.PENDING, readErrors: [] });
});

const catalogueToRequest = withDraft((draft, { category, code, result }) => {
  const { source } = findRequestSource(draft, { category, code });
  assignProps(source, { catalogue: result, catalogueReadStatus: states.SUCCESS, readErrors: [] });
});

const datasetToRequestError = withDraft((draft, action) => {
  const { item, datasets } = findRequestDatasets(draft, action);
  item.request_source = requestSources.DATASET;
  datasets.readStatus = states.ERROR;
  datasets.readErrors = [responseError(action)];
});

const fetchRequestDatasets = withDraft((draft, action) => {
  const { datasets } = findRequestDatasets(draft, action);
  datasets.readStatus = states.PENDING;
  datasets.readErrors = [];
});

const showRequestDatasets = withDraft((draft, action) => {
  const { datasets } = findRequestDatasets(draft, action);
  datasets.readStatus = states.SUCCESS;
  datasets.readErrors = [];
  datasets.results = action.append
    ? mapResults(datasets.results, action, selectableResultWithDates)
    : mapResults([], action, selectableResultWithDates);
  datasets.pageSize = action.pageSize;
  datasets.count = action.resultsCount;
  datasets.currentPage = action.currentPage;
  datasets.totalPages = action.totalPages;
});

const updateRequestDatasetsFlags = withDraft((draft, {
  category,
  code,
  internalFlag,
  privateFlag,
  publicFlag,
}) => {
  const { datasets } = findRequestDatasets(draft, { category, code });
  datasets.currentPage = 1;
  datasets.internalFlag = internalFlag;
  datasets.privateFlag = privateFlag;
  datasets.publicFlag = publicFlag;
  datasets.readErrors = [];
  datasets.readStatus = states.PENDING;
});

const updateRequestDatasetsFilter = withDraft((draft, { category, code, value }) => {
  const { datasets } = findRequestDatasets(draft, { category, code });
  datasets.filter = value;
});

const toggleRequestDatasetSelected = withDraft((draft, { category, code, datasetCode }) => {
  const { datasets } = findRequestDatasets(draft, { category, code });
  const dataset = findResultInCategory(datasets, datasetCode);
  const selectedDatasets = findSelected(datasets.results);
  selectedDatasets.forEach((d) => assignValue(d, 'selected', false));
  dataset.selected = !dataset.selected;
});

export default {
  [actions.CATALOGUE_TO_REQUEST]: catalogueToRequest,
  [actions.DATASET_SETTINGS_TO_REQUEST]: datasetSettingsToRequest,
  [actions.DATASET_TO_REQUEST_ERROR]: datasetToRequestError,
  [actions.DICTIONARIES_TO_REQUEST]: dictionariesToRequest,
  [actions.FETCH_REQUEST_CATALOGUE]: fetchRequestCatalogue,
  [actions.FETCH_REQUEST_DATASETS]: fetchRequestDatasets,
  [actions.FETCH_REQUEST_DATASET_SETTINGS]: fetchRequestDatasetSettings,
  [actions.FETCH_REQUEST_DICTIONARIES]: fetchRequestDictionaries,
  [actions.SHOW_REQUEST_DATASETS]: showRequestDatasets,
  [actions.TOGGLE_REQUEST_DATASET_SELECTED]: toggleRequestDatasetSelected,
  [actions.UPDATE_REQUEST_DATASETS_FLAGS]: updateRequestDatasetsFlags,
  [actions.UPDATE_REQUEST_DATASETS_FILTER]: updateRequestDatasetsFilter,
  [actions.UPDATE_REQUEST_DICTIONARIES]: updateRequestDictionaries,
};
